/**
 * The multi-badge certificate: one document that lists several badges, printed
 * in a single job. It is an adaptation of the single-badge certificate in
 * `certificate.ts` and shares its template bands, fonts and geometry.
 *
 * Middle-region layout (template pixels, between the top band ending at 417
 * and the footer starting at 1328):
 *
 *   title      the digger's name, in the template orange (same slot as the
 *              badge name on the single certificate)
 *   intro      "Certifica-se que … conquistou as seguintes credenciais:"
 *   rows       up to ROWS_PER_PAGE badges, each a small universe tile, the
 *              badge name in bold and its date underneath
 *   skills     on the last page only: the union of every badge's labels
 *   page no.   "Página i de n", only when there is more than one page
 *
 * Unlike the single certificate, which keeps the proportions of the template,
 * a page here has the proportions of the A4 sheet it is printed on: the header
 * band at the top of the sheet and the footer band, with the logos every page
 * must carry, at the bottom of it. Printing goes through the browser's print
 * dialog, so the digger can also "Guardar como PDF" from it.
 */
import type { ParticipantBadge } from 'shared';
import { BADGE_UNIVERSOS, hasCertificate } from 'shared';
import {
  CENTER_X,
  COLORS,
  LAYOUT,
  type Run,
  canvasToPng,
  distinctLabels,
  drawLines,
  drawTitle,
  ensureFonts,
  font,
  formatCertificateDate,
  loadImage,
  loadTemplateBands,
  openPage,
  universoTileSrc,
  wrapToFit,
} from './certificate';

/** Badges listed per page. Five leave room for the skills paragraph. */
export const ROWS_PER_PAGE = 5;

/**
 * Height of a page, in template pixels: that of an A4 sheet as wide as the
 * template. A pixel short of the exact 1736.7, so that a page scaled to the
 * width of the sheet is never a hair taller than it and pushed to the next.
 */
export const SUMMARY_PAGE_H = 1736;

const FOOTER_TOP = SUMMARY_PAGE_H - LAYOUT.footer.h;

export const SUMMARY_LAYOUT = {
  intro: { centerBaseline: 590, lineHeight: 40, size: 32, minSize: 22, maxWidth: 960, maxLines: 2 },
  rows: {
    top: 660,
    height: 110,
    /** The widest the row block may be. It is centred on CENTER_X. */
    maxWidth: 800,
    tile: { size: 84, dy: 8 },
    name: { dx: 106, baseline: 46, size: 30 },
    date: { dx: 106, baseline: 82, size: 24 },
  },
  skills: { gap: 60, lineHeight: 36, size: 28, minSize: 22, maxWidth: 960, maxLines: 4 },
  pageNumber: { baseline: FOOTER_TOP - 28, size: 22 },
} as const;

/**
 * Where the row block starts, so that it sits centred under the title: the
 * block is as wide as its widest row, whatever page that row is on, so every
 * page starts its rows at the same place.
 */
export const rowsLeft = (widestText: number): number => {
  const { maxWidth, name } = SUMMARY_LAYOUT.rows;
  const width = Math.min(maxWidth, name.dx + Math.max(0, widestText));
  return Math.round(CENTER_X - width / 2);
};

/** Splits a list into pages of `perPage` items, in order. */
export const paginate = <T>(items: readonly T[], perPage: number = ROWS_PER_PAGE): T[][] => {
  const pages: T[][] = [];
  for (let i = 0; i < items.length; i += perPage) {
    pages.push(items.slice(i, i + perPage));
  }
  return pages;
};

/**
 * The badges that belong on the summary, ordered by universe (display order,
 * unclassified last) and then by assignment date, oldest first — the order a
 * digger earned them in.
 */
export const orderForSummary = (badges: readonly ParticipantBadge[]): ParticipantBadge[] => {
  const rank = (b: ParticipantBadge): number => {
    const i = b.badgeUniverso ? BADGE_UNIVERSOS.indexOf(b.badgeUniverso) : -1;
    return i === -1 ? BADGE_UNIVERSOS.length : i;
  };
  return badges
    .filter(hasCertificate)
    .slice()
    .sort((a, b) => rank(a) - rank(b) || a.assignedAt.localeCompare(b.assignedAt));
};

/**
 * Every label across the badges, once each, in first-seen order: two badges
 * that taught the same competência name it once. This is the "competências"
 * sentence of the summary.
 */
export const uniqueLabels = (badges: readonly ParticipantBadge[]): string[] =>
  distinctLabels(badges.flatMap((b) => b.badgeLabels ?? []));

/** The sentence above the rows. */
export const buildIntroRuns = (participantName: string): Run[] => [
  { text: 'Certifica-se que ', bold: false },
  { text: participantName.trim(), bold: true },
  { text: ' conquistou as seguintes credenciais DIGGER CDI:', bold: false },
];

/** The sentence under the rows, or null when there is nothing to list. */
export const buildSkillsRuns = (labels: readonly string[]): Run[] | null =>
  labels.length === 0
    ? null
    : [
        { text: 'Trabalhou exemplarmente as seguintes competências: ', bold: false },
        { text: `${labels.join(', ')}.`, bold: true },
      ];

const loadTiles = async (
  badges: readonly ParticipantBadge[],
): Promise<Map<string, HTMLImageElement>> => {
  const srcs = new Set<string>();
  for (const b of badges) {
    const src = universoTileSrc(b.badgeUniverso);
    if (src) srcs.add(src);
  }
  const entries = await Promise.all(
    [...srcs].map(async (src) => [src, await loadImage(src)] as const),
  );
  return new Map(entries);
};

/**
 * Renders the summary, one PNG blob per page. Rejects when no badge qualifies,
 * so callers never print a certificate that certifies nothing.
 */
export const renderSummaryPages = async (
  badges: readonly ParticipantBadge[],
  participantName: string,
  template: string,
): Promise<Blob[]> => {
  const ordered = orderForSummary(badges);
  if (ordered.length === 0) {
    throw new Error('Nenhum dos badges selecionados tem certificado.');
  }

  const [bands, tiles] = await Promise.all([loadTemplateBands(template), loadTiles(ordered)]);
  const L = SUMMARY_LAYOUT;
  await ensureFonts([
    LAYOUT.title.size,
    L.intro.size,
    L.rows.name.size,
    L.rows.date.size,
    L.skills.size,
    L.pageNumber.size,
  ]);

  const pages = paginate(ordered);
  const skills = buildSkillsRuns(uniqueLabels(ordered));
  const blobs: Blob[] = [];
  const textMaxWidth = L.rows.maxWidth - L.rows.name.dx;
  let left: number | null = null;

  for (const [pageIndex, rows] of pages.entries()) {
    const { canvas, ctx } = openPage(bands, SUMMARY_PAGE_H);
    const isLast = pageIndex === pages.length - 1;

    if (left === null) {
      const widths = ordered.flatMap((badge) => {
        ctx.font = font(L.rows.name.size, true);
        const name = ctx.measureText(badge.badgeName).width;
        ctx.font = font(L.rows.date.size, false);
        return [name, ctx.measureText(formatCertificateDate(badge.assignedAt)).width];
      });
      left = rowsLeft(Math.min(textMaxWidth, Math.max(...widths)));
    }
    const rowX = left;

    drawTitle(ctx, participantName.trim());

    // ── Intro ────────────────────────────────────────────────────────────
    ctx.fillStyle = COLORS.body;
    const intro = wrapToFit(ctx, buildIntroRuns(participantName), L.intro);
    const introSpan = (intro.lines.length - 1) * L.intro.lineHeight;
    drawLines(ctx, intro.lines, intro.size, L.intro.centerBaseline - introSpan / 2, L.intro.lineHeight);

    // ── Rows ─────────────────────────────────────────────────────────────
    ctx.textAlign = 'left';
    ctx.textBaseline = 'alphabetic';
    rows.forEach((badge, i) => {
      const top = L.rows.top + i * L.rows.height;
      const tileSrc = universoTileSrc(badge.badgeUniverso);
      const tile = tileSrc ? tiles.get(tileSrc) : undefined;
      if (tile) {
        ctx.drawImage(tile, rowX, top + L.rows.tile.dy, L.rows.tile.size, L.rows.tile.size);
      }
      ctx.fillStyle = COLORS.body;
      ctx.font = font(L.rows.name.size, true);
      ctx.fillText(badge.badgeName, rowX + L.rows.name.dx, top + L.rows.name.baseline, textMaxWidth);
      ctx.font = font(L.rows.date.size, false);
      ctx.fillText(
        formatCertificateDate(badge.assignedAt),
        rowX + L.rows.date.dx,
        top + L.rows.date.baseline,
        textMaxWidth,
      );
    });

    // ── Skills (last page) ───────────────────────────────────────────────
    if (isLast && skills) {
      const rowsBottom = L.rows.top + rows.length * L.rows.height;
      const wrapped = wrapToFit(ctx, skills, L.skills);
      ctx.fillStyle = COLORS.body;
      drawLines(ctx, wrapped.lines, wrapped.size, rowsBottom + L.skills.gap, L.skills.lineHeight);
    }

    // ── Page number ──────────────────────────────────────────────────────
    if (pages.length > 1) {
      ctx.fillStyle = COLORS.body;
      ctx.font = font(L.pageNumber.size, false);
      ctx.textAlign = 'center';
      ctx.fillText(`Página ${pageIndex + 1} de ${pages.length}`, CENTER_X, L.pageNumber.baseline);
    }

    blobs.push(await canvasToPng(canvas));
  }

  return blobs;
};

const PRINT_CONTAINER_CLASS = 'certificate-print';
const PRINT_PAGE_CLASS = 'certificate-print__page';

/** Some mobile browsers never fire `afterprint`; tidy up eventually anyway. */
const PRINT_CLEANUP_FALLBACK_MS = 5 * 60 * 1000;

const removePrintContainers = (): void => {
  document.querySelectorAll(`.${PRINT_CONTAINER_CLASS}`).forEach((el) => {
    el.querySelectorAll('img').forEach((img) => URL.revokeObjectURL(img.src));
    el.remove();
  });
};

/** Resolves once the image is decoded, or immediately where decode() is unavailable. */
const settled = (img: HTMLImageElement): Promise<void> =>
  typeof img.decode === 'function' ? img.decode().catch(() => undefined) : Promise.resolve();

/**
 * Puts the pages in a print-only container (see `.certificate-print` in
 * index.css, which hides the app and gives each page a sheet to itself) and
 * opens the browser's print dialog.
 */
export const printPages = async (pages: readonly Blob[]): Promise<void> => {
  removePrintContainers();
  const container = document.createElement('div');
  container.className = PRINT_CONTAINER_CLASS;
  container.setAttribute('aria-hidden', 'true');

  const imgs = pages.map((blob, i) => {
    const img = document.createElement('img');
    img.className = PRINT_PAGE_CLASS;
    img.alt = `Certificado, página ${i + 1}`;
    img.src = URL.createObjectURL(blob);
    container.appendChild(img);
    return img;
  });
  document.body.appendChild(container);
  await Promise.all(imgs.map(settled));

  const timer = setTimeout(removePrintContainers, PRINT_CLEANUP_FALLBACK_MS);
  window.addEventListener(
    'afterprint',
    () => {
      clearTimeout(timer);
      removePrintContainers();
    },
    { once: true },
  );
  window.print();
};

/** Renders the summary for the given badges and sends it to print. */
export const printBadgeSummary = async (
  badges: readonly ParticipantBadge[],
  participantName: string,
  template: string,
): Promise<void> => {
  const pages = await renderSummaryPages(badges, participantName, template);
  await printPages(pages);
};
